import React from "react"
import { cn } from "../lib/utils"
import { filterSections, visibleModules } from "../lib/book-nav"
import { AppDialogProvider } from "./app-dialog-provider"
import { TopNav } from "./top-nav"
import { PageLoadingProvider } from "../lib/page-loading"

const SECTION_NAV = {
  sales: [
    { slug: "customers", label: "Customers" },
    { slug: "quotes", label: "Quotes" },
    { slug: "sales-orders", label: "Sales Orders" },
    { slug: "invoices", label: "Invoices" },
    { slug: "recurring-invoices", label: "Recurring Invoices" },
    { slug: "delivery-challans", label: "Delivery Challans" },
    { slug: "payments-received", label: "Payments Received" },
    { slug: "credit-notes", label: "Credit Notes" },
  ],
  purchases: [
    { slug: "vendors", label: "Vendors" },
    { slug: "requisitions", label: "Requisitions" },
    { slug: "vendor-quotes", label: "Vendor Quotes" },
    { slug: "expenses", label: "Expenses" },
    { slug: "recurring-expenses", label: "Recurring Expenses" },
    { slug: "purchase-orders", label: "Purchase Orders" },
    { slug: "bills", label: "Bills" },
    { slug: "recurring-bills", label: "Recurring Bills" },
    { slug: "payments-made", label: "Payments Made" },
    { slug: "vendor-credits", label: "Vendor Credits" },
  ],
  accountant: [
    { slug: "manual-journals", label: "Manual Journals" },
    { slug: "trial-balance", label: "Trial Balance" },
    { slug: "general-ledger", label: "General Ledger" },
    { slug: "income-statement", label: "Income Statement" },
    { slug: "gstr1-sales", label: "GSTR-1 sales (India)" },
    { slug: "gstr2-purchases", label: "GSTR-2 purchases / ITC (India)" },
    { slug: "bulk-update", label: "Bulk Update" },
    { slug: "currency-adjustments", label: "Currency Adjustments" },
    { slug: "chart-of-accounts", label: "Chart of Accounts" },
    { slug: "bookkeeping-settings", label: "Bookkeeping" },
    { slug: "fiscal-periods", label: "Fiscal Periods" },
    { slug: "budgets", label: "Budgets" },
    { slug: "transaction-locking", label: "Transaction Locking" },
    { slug: "payroll", label: "Payroll & salaries" },
  ],
  "credit-notes": [
    { slug: "all", label: "All Credit Notes" },
    { slug: "applied", label: "Applied Credits" },
    { slug: "preferences", label: "Preferences" },
  ],
  documents: [
    { slug: "library", label: "Document Library" },
    { slug: "folders", label: "Folders" },
    { slug: "upload", label: "Upload" },
  ],
  "time-tracking": [
    { slug: "projects", label: "Projects" },
    { slug: "timesheets", label: "Timesheets" },
    { slug: "timer", label: "Timer" },
  ],
  items: [{ slug: "catalog", label: "Item master" }],
  "employee-management": [
    { slug: "employees", label: "Employee registration" },
    { slug: "salary-structures", label: "Salary structures" },
    { slug: "banking", label: "Bank account details" },
    { slug: "nominees", label: "Nominee details" },
  ],
}

export function BookShell({ activeSlug, userName, children, access = {}, featureFlags = {}, contentClassName }) {
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
  const pathname = typeof window !== "undefined" ? window.location.pathname : ""
  const modules = visibleModules(access)
  const setupOnly = Boolean(access?.needs_company)
  const baseSections = SECTION_NAV[activeSlug] || []
  const gatedSections =
    activeSlug === "purchases" && !featureFlags.vendor_quote_reselling
      ? baseSections.filter((item) => item.slug !== "vendor-quotes")
      : baseSections
  const sectionItems = filterSections(access, activeSlug, gatedSections)

  return (
    <AppDialogProvider>
      <PageLoadingProvider>
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <TopNav userName={userName} access={access} />
        <div className="grid min-h-0 flex-1 grid-cols-[232px_1fr]">
          <aside className="min-h-0 border-r border-sidebar-border bg-sidebar shadow-nav">
            <div className="flex h-full flex-col px-2.5 py-4">
              <div className="mb-3 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {setupOnly ? "Get started" : "Modules"}
              </div>
              <nav className="flex-1 space-y-0.5 overflow-y-auto">
                {setupOnly ? (
                  <p className="px-2.5 py-2 text-[13px] leading-relaxed text-muted-foreground">
                    Register your company to unlock Sales, Purchases, Banking, and the rest of Actinium Book.
                  </p>
                ) : (
                modules.map(({ name, slug, href }) => {
                  const isActive = activeSlug === slug
                  return (
                    <div key={slug}>
                      <a
                        href={href}
                        className={cn(
                          "block rounded-lg px-2.5 py-2 text-sm text-sidebar-foreground/90 transition-colors hover:bg-muted hover:text-foreground",
                          isActive && "bg-sidebar-accent font-semibold text-primary shadow-sm",
                        )}
                      >
                        {name}
                      </a>
                      {isActive && sectionItems.length > 0 ? (
                        <div className="mt-1 space-y-0.5 border-l-2 border-primary/25 py-0.5 pl-2.5 ml-2.5">
                          {sectionItems.map((item) => {
                            const sectionHref = `/${activeSlug}/${item.slug}`
                            const sectionActive = pathname === sectionHref
                            return (
                              <a
                                key={item.slug}
                                href={sectionHref}
                                className={cn(
                                  "block rounded-md py-1.5 pl-2 pr-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                  sectionActive && "bg-primary/10 font-medium text-primary",
                                )}
                              >
                                {item.label}
                              </a>
                            )
                          })}
                        </div>
                      ) : null}
                    </div>
                  )
                })
                )}
              </nav>
              {access?.can_manage_company_settings || access?.can_create_company ? (
                <div className="mt-3 px-2">
                  <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Administration</div>
                  <a
                    href="/admin"
                    className={cn(
                      "block rounded-lg px-2.5 py-2 text-sm text-sidebar-foreground/90 transition-colors hover:bg-muted hover:text-foreground",
                      pathname === "/admin" && "bg-sidebar-accent font-semibold text-primary shadow-sm",
                    )}
                  >
                    {access?.needs_company ? "Register company" : "Organization & team"}
                  </a>
                </div>
              ) : null}
              <div className="mt-4 border-t border-sidebar-border pt-3 text-[13px] leading-snug text-muted-foreground">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">{userName}</span>
                  {access?.access_role_label || access?.access_role ? (
                    <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground/80">
                      {access.access_role_label || access.access_role}
                    </span>
                  ) : null}
                </div>
                <form action="/logout" method="post" className="mt-2">
                  {csrfToken ? <input type="hidden" name="authenticity_token" value={csrfToken} /> : null}
                  <input type="hidden" name="_method" value="delete" />
                  <button
                    type="submit"
                    className="w-full rounded-lg border border-border bg-card px-2 py-2 text-[13px] font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
                  >
                    Log out
                  </button>
                </form>
              </div>
            </div>
          </aside>
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-muted/30 text-sm text-foreground antialiased">
            <div className={cn("mx-auto w-full max-w-none space-y-6 px-4 py-5 md:px-6 md:py-6", contentClassName)}>{children}</div>
          </main>
        </div>
      </div>
      </PageLoadingProvider>
    </AppDialogProvider>
  )
}
